import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Card, Row, Col, Space, Badge, Alert, Button, message, Modal, theme, Dropdown, Popover, TreeSelect, Skeleton } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { RefreshIcon } from '../common/ActionIcons';
import { ThemeContext } from '../../index';
import ErrorStats from './ErrorStats.component';
import Bulletins from './Bulletins.component';
import NiFiProcessGroup from './NiFiProcessGroup.component';
import { PiGraphBold } from "react-icons/pi";
import { AiFillExclamationCircle } from "react-icons/ai";
import { getFlowStatus, startProcessGroup, stopProcessGroup, emptyAllQueues, getProcessGroup, buildProcessGroupTree, findProcessGroupPathInTree } from './utils/nifiHelper';
import { useSelector } from 'react-redux';

const CARD_HEIGHT = 340;

const NiFiFlowMonitor = () => {
  const { token } = theme.useToken();
  const { themeConfig } = useContext(ThemeContext);
  const userInfo = useSelector(state => state.userLogin?.userInfo) || JSON.parse(localStorage.getItem('userInfo'));
  const orgProcessGroupId = userInfo?.nifiProcessGroupId || process.env.REACT_APP_NIFI_PROCESS_GROUP_ID || '';
  const isDarkMode = themeConfig.algorithm === 'dark';
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProcessGroupId, setSelectedProcessGroupId] = useState(orgProcessGroupId || '');
  const [actionLoading, setActionLoading] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasBulletins, setHasBulletins] = useState(false);
  const [processGroupTree, setProcessGroupTree] = useState([]);

  const selectedGroupPath = useMemo(
    () => findProcessGroupPathInTree(processGroupTree, selectedProcessGroupId),
    [processGroupTree, selectedProcessGroupId]
  );

  const fetchProcessGroupTree = async () => {
    const nifiUsername = userInfo?.nifiUsername;
    const nifiPassword = userInfo?.nifiPassword;
    if (!nifiUsername || !nifiPassword || !orgProcessGroupId) {
      setProcessGroupTree([]);
      return;
    }
    try {
      const tree = await buildProcessGroupTree(nifiUsername, nifiPassword, orgProcessGroupId);
      setProcessGroupTree(tree);
    } catch {
      setProcessGroupTree([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const nifiUsername = userInfo?.nifiUsername;
      const nifiPassword = userInfo?.nifiPassword;
      
      if (!nifiUsername || !nifiPassword) {
        throw new Error('NiFi credentials not configured for this organization');
      }
      
      await getFlowStatus(nifiUsername, nifiPassword);
      setIsConnected(true);
      await fetchProcessGroupTree();
      
      if (selectedProcessGroupId) {
        try {
          const pgData = await getProcessGroup(nifiUsername, nifiPassword, selectedProcessGroupId);
          const runningCount = pgData?.runningCount || pgData?.component?.runningCount || 0;
          setIsRunning(runningCount > 0);
        } catch {
          setIsRunning(false);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to NiFi');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedProcessGroupId]);

  const handleRefresh = () => {
    fetchData();
  };

  const handleProcessGroupSelect = (groupId) => {
    if (groupId) {
      setSelectedProcessGroupId(groupId);
    }
  };

  const handleStartStop = async () => {
    const nifiUsername = userInfo?.nifiUsername;
    const nifiPassword = userInfo?.nifiPassword;
    
    if (!nifiUsername || !nifiPassword) {
      message.error('NiFi credentials not configured');
      return;
    }

    if (!selectedProcessGroupId) {
      message.error('No process group selected');
      return;
    }

    setActionLoading(isRunning ? 'stop' : 'start');

    try {
      let result;
      if (isRunning) {
        result = await stopProcessGroup(nifiUsername, nifiPassword, selectedProcessGroupId);
        if (result.success) {
          message.success('Process group stopped successfully');
          setIsRunning(false);
        } else {
          throw new Error('Failed to stop process group');
        }
      } else {
        result = await startProcessGroup(nifiUsername, nifiPassword, selectedProcessGroupId);
        if (result.success) {
          message.success('Process group started successfully');
          setIsRunning(true);
        } else {
          throw new Error('Failed to start process group');
        }
      }
      await fetchData();
    } catch (err) {
      message.error(`Failed to ${isRunning ? 'stop' : 'start'} process group: ${err.message}`);
      await fetchData();
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmptyQueues = async () => {
    const nifiUsername = userInfo?.nifiUsername;
    const nifiPassword = userInfo?.nifiPassword;
    
    if (!nifiUsername || !nifiPassword) {
      message.error('NiFi credentials not configured');
      return;
    }

    if (!selectedProcessGroupId) {
      message.error('No process group selected');
      return;
    }

    setActionLoading('empty');

    try {
      const result = await emptyAllQueues(nifiUsername, nifiPassword, selectedProcessGroupId);
      
      if (result.failureCount > 0) {
        message.warning(`Emptied ${result.successCount}/${result.totalConnections} queues. ${result.failureCount} failed.`);
      } else {
        message.success(`Successfully emptied all ${result.totalConnections} queues`);
      }
      
      handleRefresh();
    } catch (err) {
      message.error(`Failed to empty queues: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEmptyQueuesWithConfirm = () => {
    Modal.confirm({
      title: 'Empty all queues?',
      content: 'This will remove all flowfiles from all queues in this process group. This action cannot be undone.',
      okText: 'Yes, empty queues',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: handleEmptyQueues,
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '200px', width: '100%' }}>
        <Card size="small">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Skeleton active title={{ width: '35%' }} paragraph={{ rows: 1, width: ['65%'] }} />
            <Skeleton active paragraph={{ rows: 5 }} />
          </Space>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" message={error} style={{ margin: '20px 0' }} />
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ height: '100%' }}>
      <Col span={24}>
        <Card
          title={
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <PiGraphBold />
                <span>Process Group Flow</span>
                {hasBulletins && (
                  <Popover
                    content={
                      <div style={{ width: '600px', maxHeight: '500px', overflowY: 'auto' }}>
                        <Bulletins 
                          onRefresh={handleRefresh}
                          onDataChange={setHasBulletins}
                          userInfo={userInfo}
                        />
                      </div>
                    }
                    title="Bulletins"
                    trigger="click"
                    placement="bottomLeft"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<AiFillExclamationCircle style={{ color: '#ff4d4f' }} />}
                      style={{ padding: 0, height: 'auto' }}
                    />
                  </Popover>
                )}
                <Badge 
                  status={isConnected ? "success" : "error"} 
                  text={isConnected ? "Connected" : "Disconnected"} 
                />
              </Space>
              <Space size="small">
                <span style={{ fontSize: '12px', color: '#666' }}>Process Group:</span>
                {orgProcessGroupId && (
                  <TreeSelect
                    size="small"
                    style={{ minWidth: 280, maxWidth: 420 }}
                    placeholder="Select process group"
                    treeData={processGroupTree}
                    value={selectedProcessGroupId || undefined}
                    onChange={handleProcessGroupSelect}
                    treeDefaultExpandAll
                    showSearch
                    treeNodeFilterProp="title"
                    popupMatchSelectWidth={false}
                  />
                )}
                <Button 
                  type="text" 
                  icon={<RefreshIcon />} 
                  onClick={handleRefresh}
                  disabled={loading}
                  title="Refresh NiFi data"
                  size="small"
                >
                  Refresh
                </Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'startStop',
                        label: isRunning ? 'Stop Process Group' : 'Start Process Group',
                        icon: isRunning ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
                        onClick: handleStartStop,
                        disabled: !selectedProcessGroupId || actionLoading === 'start' || actionLoading === 'stop',
                        danger: isRunning,
                      },
                      {
                        key: 'emptyQueues',
                        label: 'Empty All Queues',
                        icon: <DeleteOutlined />,
                        onClick: handleEmptyQueuesWithConfirm,
                        disabled: !selectedProcessGroupId || actionLoading === 'empty',
                        danger: true,
                      },
                    ],
                  }}
                  trigger={['click']}
                  disabled={!selectedProcessGroupId}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreOutlined />}
                    title="More actions"
                  />
                </Dropdown>
              </Space>
            </Space>
          }
          size="small"
          style={{
            height: '100%',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}
          bodyStyle={{ 
            padding: '16px',
            height: 'calc(100% - 57px)',
            overflow: 'hidden'
          }}
        >
          <NiFiProcessGroup 
            processGroupId={selectedProcessGroupId}
            groupPath={selectedGroupPath}
            onRefresh={handleRefresh}
            userInfo={userInfo}
            onStartStop={handleStartStop}
            onEmptyQueues={handleEmptyQueues}
            actionLoading={actionLoading}
            isRunning={isRunning}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          title="System Resource Monitoring"
          size="small"
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            marginTop: '16px'
          }}
          bodyStyle={{ 
            padding: '16px'
          }}
        >
          <ErrorStats 
            onRefresh={handleRefresh}
            userInfo={userInfo}
          />
        </Card>
      </Col>
      {/* Hidden Bulletins to check for items */}
      <div style={{ display: 'none' }}>
        <Bulletins 
          onRefresh={handleRefresh}
          onDataChange={setHasBulletins}
          userInfo={userInfo}
        />
      </div>
    </Row>
  );
};

export default NiFiFlowMonitor;
